import { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Banner, Buttons, FlexContainer, ModalContext, Modals, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { showChat } from 'Services/chat';
import { showModalAction } from 'Store/modules/app/actions';
import useTranslation from 'Hooks/useTranslation';
import { useGetUserSpeiOut } from 'Containers/Dashboard/hooks';
import { useGetUserCustomerData } from 'Containers/Datagathering/views/DgTypeForm/hooks';
import WatchClabeBanner from 'Components/WatchClabeBanner';
import { lottiesPaths } from 'Constants/lottiesPaths';
import { triggerModalForReview } from 'Utils/native';
import { PDF_MODAL } from 'Constants/modals';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import { ResultTypes, TaxScreenType } from 'Types/common';
import ResultInfo from '../ResultInfo';
import * as Styled from '../styles';
import SpeiOutInfo from '../SpeiOutInfo';
import { useGetCep } from './hooks';

const CEP_UNAVAILABLE = 'unavailable';

const Paid = ({ tax: { taxYear, filingInfo, pdf } }: TaxScreenType): JSX.Element => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const dispatch = useDispatch();
  const { showModal, hideModal } = useContext(ModalContext);
  const customerInfo = useGetUserCustomerData();
  const plan = useMemo(
    () =>
      customerInfo?.activePlan?.name
        ? plansNameMapping(customerInfo.activePlan.name)
        : FRONT_PLANS.BASIC,
    [customerInfo]
  );
  const isFlex = plan && plan === FRONT_PLANS.FLEX;
  const { isLoading, data } = useGetUserSpeiOut(isFlex);
  const { refetch } = useGetCep({ trackId: data?.trackId });

  const handleClick = (): void => {
    if (!data) {
      return;
    }
    showModal({
      comp: (
        <Modals.Centered maxSize="XL" hasClose onClose={hideModal}>
          <SpeiOutInfo info={data} />
        </Modals.Centered>
      ),
    });
  };

  useEffect(() => {
    triggerModalForReview();
  }, []);

  const handleViewCep = async (): Promise<void> => {
    const response = await refetch();
    if (response.isSuccess) {
      if (response?.data?.data?.item?.kind === CEP_UNAVAILABLE) {
        handleClick();
      }
      if (response.data?.data?.item?.fileUrl) {
        dispatch(
          showModalAction({
            options: {
              type: PDF_MODAL,
              cepFile: response.data?.data?.item?.fileUrl,
              pdf: response.data?.data?.item?.fileUrl,
            },
          })
        );
      }
    }
  };

  const handleShowDraft = (): void => {
    dispatch(showModalAction({ options: { type: PDF_MODAL, pdf } }));
  };

  return (
    <FlexContainer direction="column" fullHeight gap="dimension_16" fullWidth>
      {filingInfo?.isPartial && (
        <Banner
          hasIcon
          label={t(`Dashboard.Paid.banner.title`)}
          description={t(`Dashboard.Paid.banner.description`)}
          mode="warning"
          id="pending-banner"
          layout="embed"
        />
      )}
      <Styled.InfoContainer
        justify="center"
        direction="column"
        align="center"
        gap="dimension_08"
        fullHeight
      >
        <Text size="L_L_S" color="dark" type="headline">
          {taxYear}
        </Text>
        <Text type="paragraphs" size="L_L_M" color="dark" align="center">
          {t('Dashboard.Paid.message')}
        </Text>
        <Styled.StyledLottie
          options={{
            path: lottiesPaths.PAID,
            rendererSetting: { progressiveLoad: true },
          }}
        />
        {filingInfo && <ResultInfo filingInfo={filingInfo} />}
        <Text type="paragraphs" color="dark" size="M_L_S" align="center">
          {t(`Dashboard.Paid.title`, { context: filingInfo?.isPartial && 'isPartial' })}
        </Text>
        <Text type="paragraphs" color="dark" size="S_S_S" align="center">
          {t(`Dashboard.Paid.description`, { context: filingInfo?.isPartial && 'isPartial' })}
        </Text>
        <Styled.ButtonsWrapper direction="column">
          <FlexContainer direction="column" gap="dimension_16">
            <Styled.MultipleButtonContainer
              direction="row-reverse"
              gap="dimension_16"
              justify="center"
            >
              {data && !isLoading && (
                <Buttons.Classic
                  size="m"
                  mode="primary"
                  onClick={handleViewCep}
                  label={t('Dashboard.Paid.cep')}
                />
              )}
              {pdf && (
                <Buttons.Classic
                  size="m"
                  mode={data ? 'light' : 'primary'}
                  onClick={handleShowDraft}
                  label={t('Dashboard.Paid.firstButton')}
                />
              )}
              {filingInfo?.isPartial && !data && !isFlex && (
                <Buttons.Classic
                  size="m"
                  mode="light"
                  label={t('Dashboard.Paid.help.button')}
                  onClick={showChat}
                />
              )}
            </Styled.MultipleButtonContainer>
          </FlexContainer>
          {filingInfo?.recent.taxReturnType === ResultTypes.toReturn && <WatchClabeBanner />}
        </Styled.ButtonsWrapper>
      </Styled.InfoContainer>
    </FlexContainer>
  );
};

export default Paid;
